import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import {
  useSubscriptionContext,
  callFunction,
  PLAN_LIMITS,
  PLAN_DISPLAY_NAMES,
} from '@platform/core';
import type {
  PlanTier,
  BillingInterval,
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  CreatePortalSessionRequest,
  CreatePortalSessionResponse,
} from '@platform/core';

const ALL_PLANS: PlanTier[] = ['starter', 'professional', 'business', 'enterprise', 'enterprise_plus'];

// Initialize Paddle if client token is available
function initPaddle() {
  const clientToken = import.meta.env.VITE_PADDLE_CLIENT_TOKEN;
  if (clientToken && window.Paddle) {
    window.Paddle.Setup({
      token: clientToken,
      environment: import.meta.env.VITE_PADDLE_ENVIRONMENT || 'sandbox',
    });
  }
}

export function BillingPage() {
  const {
    subscription,
    loading,
    isTrialing,
    isActive,
    isPastDue,
    isCancelled,
    isExpired,
    trialDaysLeft,
    planLimits,
  } = useSubscriptionContext();

  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly');

  // Usage counts
  const [usersCount, setUsersCount] = useState<number>(0);
  const [companiesCount, setCompaniesCount] = useState<number>(0);
  const [usageLoading, setUsageLoading] = useState(true);

  const organizationId = subscription?.organizationId || '';

  // Initialize Paddle on mount
  useEffect(() => {
    initPaddle();
  }, []);

  // Fetch real usage counts
  useEffect(() => {
    if (!organizationId) {
      setUsageLoading(false);
      return;
    }

    let cancelled = false;
    setUsageLoading(true);

    callFunction<{ organizationId: string }, { usersCount: number; companiesCount: number }>(
      'getOrganizationUsage',
      { organizationId }
    )
      .then((result) => {
        if (!cancelled) {
          setUsersCount(result.usersCount);
          setCompaniesCount(result.companiesCount);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to fetch usage:', err);
        }
      })
      .finally(() => {
        if (!cancelled) setUsageLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const currentPlan = subscription?.plan || 'starter';

  const handleUpgrade = async (plan: PlanTier) => {
    if (!organizationId) return;
    setCheckoutLoading(plan);
    setError(null);

    try {
      const result = await callFunction<CreateCheckoutSessionRequest, CreateCheckoutSessionResponse>(
        'createCheckoutSession',
        { plan, organizationId, billingInterval }
      );

      if (window.Paddle && result.transactionId) {
        window.Paddle.Checkout.open({ transactionId: result.transactionId });
      } else if (result.checkoutUrl) {
        window.open(result.checkoutUrl, '_blank');
      } else if (result.transactionId) {
        setError('Paddle checkout overlay not yet configured. Transaction ID: ' + result.transactionId);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create checkout session';
      setError(message);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    if (!organizationId) return;
    setPortalLoading(true);
    setError(null);

    try {
      const result = await callFunction<CreatePortalSessionRequest, CreatePortalSessionResponse>(
        'createPortalSession',
        { organizationId }
      );

      if (result.portalUrl) {
        window.open(result.portalUrl, '_blank');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to open subscription portal';
      setError(message);
    } finally {
      setPortalLoading(false);
    }
  };

  const getStatusChip = () => {
    if (isTrialing) return <Chip label={`Trial — ${trialDaysLeft} days left`} color="info" />;
    if (isActive) return <Chip label="Active" color="success" />;
    if (isPastDue) return <Chip label="Past Due" color="error" />;
    if (isCancelled) return <Chip label="Cancelled" color="default" />;
    if (isExpired) return <Chip label="Expired" color="error" />;
    return <Chip label="Unknown" color="default" />;
  };

  const usersPercent = Math.min(100, (usersCount / planLimits.maxUsers) * 100);
  const companiesPercent = planLimits.maxCompanies === -1
    ? 0
    : Math.min(100, (companiesCount / planLimits.maxCompanies) * 100);

  const getPrice = (plan: PlanTier) => {
    if (billingInterval === 'yearly') {
      return PLAN_LIMITS[plan].priceYearly;
    }
    return PLAN_LIMITS[plan].priceMonthly;
  };

  const getPriceLabel = () => {
    return billingInterval === 'yearly' ? '/year' : '/month';
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Billing & Subscription
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Current Plan */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6">
              Current Plan: {PLAN_DISPLAY_NAMES[currentPlan as PlanTier] || currentPlan}
            </Typography>
            <Box sx={{ mt: 1 }}>{getStatusChip()}</Box>
          </Box>
          {subscription?.paddleSubscriptionId && (
            <Button
              variant="outlined"
              onClick={handleManageSubscription}
              disabled={portalLoading}
            >
              {portalLoading ? <CircularProgress size={20} /> : 'Manage Subscription'}
            </Button>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Usage Stats */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" gutterBottom>
              Users: {usageLoading ? '...' : usersCount} / {planLimits.maxUsers}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={usageLoading ? 0 : usersPercent}
              color={usersPercent > 80 ? 'warning' : 'primary'}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" gutterBottom>
              Companies: {usageLoading ? '...' : companiesCount} / {planLimits.maxCompanies === -1 ? '∞' : planLimits.maxCompanies}
            </Typography>
            {planLimits.maxCompanies === -1 ? (
              <Typography variant="body2" color="text.secondary">Unlimited</Typography>
            ) : (
              <LinearProgress
                variant="determinate"
                value={usageLoading ? 0 : companiesPercent}
                color={companiesPercent > 80 ? 'warning' : 'primary'}
                sx={{ height: 8, borderRadius: 4 }}
              />
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Plan Comparison Table */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">
          Plans
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color={billingInterval === 'monthly' ? 'primary' : 'text.secondary'}>
            Monthly
          </Typography>
          <Switch
            checked={billingInterval === 'yearly'}
            onChange={(e) => setBillingInterval(e.target.checked ? 'yearly' : 'monthly')}
            color="primary"
          />
          <Typography variant="body2" color={billingInterval === 'yearly' ? 'primary' : 'text.secondary'}>
            Yearly
          </Typography>
          {billingInterval === 'yearly' && (
            <Chip label="Save 20%" color="success" size="small" sx={{ ml: 1 }} />
          )}
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Feature</TableCell>
              {ALL_PLANS.map((plan) => (
                <TableCell key={plan} align="center">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="subtitle2">
                      {PLAN_DISPLAY_NAMES[plan]}
                    </Typography>
                    {plan === 'professional' && (
                      <Chip label="Popular" color="primary" size="small" sx={{ mt: 0.5 }} />
                    )}
                    {plan === currentPlan && (
                      <Chip label="Current" color="success" size="small" variant="outlined" sx={{ mt: 0.5 }} />
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Price</TableCell>
              {ALL_PLANS.map((plan) => (
                <TableCell key={plan} align="center">
                  <Typography variant="h6" color="primary">
                    ${getPrice(plan)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {getPriceLabel()}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>Users</TableCell>
              {ALL_PLANS.map((plan) => (
                <TableCell key={plan} align="center">
                  {PLAN_LIMITS[plan].maxUsers}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>Companies</TableCell>
              {ALL_PLANS.map((plan) => (
                <TableCell key={plan} align="center">
                  {PLAN_LIMITS[plan].maxCompanies === -1 ? 'Unlimited' : PLAN_LIMITS[plan].maxCompanies}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              {ALL_PLANS.map((plan) => (
                <TableCell key={plan} align="center">
                  {plan === currentPlan ? (
                    <Button variant="outlined" disabled startIcon={<CheckCircleIcon />}>
                      Current Plan
                    </Button>
                  ) : (
                    <Button
                      variant={plan === 'professional' ? 'contained' : 'outlined'}
                      onClick={() => handleUpgrade(plan)}
                      disabled={checkoutLoading !== null}
                      startIcon={plan === 'professional' ? <StarIcon /> : undefined}
                    >
                      {checkoutLoading === plan ? (
                        <CircularProgress size={20} />
                      ) : (
                        ALL_PLANS.indexOf(plan) > ALL_PLANS.indexOf(currentPlan as PlanTier)
                          ? 'Upgrade'
                          : 'Switch'
                      )}
                    </Button>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Billing History Stub */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Billing History
        </Typography>
        <Typography color="text.secondary">
          Billing history will be available once your subscription is active.
        </Typography>
      </Paper>
    </Container>
  );
}

export default BillingPage;
